import { useCallback, type RefObject } from "react";

/**
 * Hook that handles clicks outside a container element
 * @param contentRef - Ref to the content element that should NOT trigger the callback when clicked
 * @param callback - Function to call when clicking outside the content element
 * @returns Click handler to attach to the outer container element
 */
function useClickOutside<T extends HTMLElement>(
  contentRef: RefObject<T | null>,
  callback: () => void,
) {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const contentRect = contentRef.current?.getBoundingClientRect();
      if (!contentRect) return;

      const isInContent =
        contentRect.top <= e.clientY &&
        e.clientY <= contentRect.top + contentRect.height &&
        contentRect.left <= e.clientX &&
        e.clientX <= contentRect.left + contentRect.width;

      if (!isInContent) {
        callback();
      }
    },
    [contentRef, callback],
  );

  return handleClick;
}

export default useClickOutside;
